import type { AnaliseDaAdegaDTO } from '@adega/shared';
import { useAiStatus, useAnaliseDaAdega, useGerarAnaliseDaAdega } from '../api/queries.js';
import { AiConfigBanner, ErrorBox } from './Feedback.js';

/**
 * O conselho da IA sobre a composição da adega.
 *
 * Fica depois dos números, e não antes: o diagnóstico de graça vem primeiro, e
 * só quem quiser o julgamento paga por ele. Nenhuma chamada automática — a
 * análise anterior fica guardada justamente para reabrir a tela não custar.
 */
export function AnaliseDaAdega() {
  const { data: aiStatus } = useAiStatus();
  const { data: analise, isLoading } = useAnaliseDaAdega();
  const gerar = useGerarAnaliseDaAdega();

  if (isLoading) return null;
  if (aiStatus && !aiStatus.enabled) {
    return (
      <section className="card space-y-2">
        <h2 className="font-semibold">🧭 Como está a composição</h2>
        <AiConfigBanner contexto="A leitura da composição da adega depende de IA." />
      </section>
    );
  }

  return (
    <section className="card space-y-3">
      <div>
        <h2 className="font-semibold">🧭 Como está a composição</h2>
        <p className="text-sm text-adega-muted">
          {analise
            ? 'O que falta, o que sobra e o que procurar na próxima compra.'
            : 'Uma leitura do conjunto: o que falta, o que sobra e o que procurar na próxima compra. Os números acima não são recalculados — a IA só opina sobre eles.'}
        </p>
      </div>

      {analise ? <Conteudo analise={analise} /> : null}

      {gerar.error ? <ErrorBox erro={gerar.error} /> : null}

      <button
        type="button"
        className={analise ? 'btn-secondary w-full' : 'btn-primary w-full'}
        disabled={gerar.isPending}
        onClick={() => void gerar.mutateAsync()}
      >
        {gerar.isPending
          ? 'Analisando…'
          : analise
            ? 'Atualizar análise'
            : '🧭 Analisar minha adega'}
      </button>

      {analise ? (
        <p className="text-center text-xs text-adega-muted">
          Análise de {new Date(analise.geradaEm).toLocaleDateString('pt-BR')} por{' '}
          {analise.provider}/{analise.model}. Ela não muda sozinha quando você cadastra garrafas.
        </p>
      ) : null}
    </section>
  );
}

function Conteudo({ analise }: { analise: AnaliseDaAdegaDTO }) {
  return (
    <div className="space-y-4">
      {analise.leitura ? (
        <p className="text-sm leading-relaxed">{analise.leitura}</p>
      ) : null}

      <Lista
        titulo="O que falta"
        cor="border-adega-gold/40 bg-adega-gold/5"
        itens={analise.lacunas.map((item) => ({
          titulo: item.titulo,
          corpo: item.porque,
          extra: item.sugestao,
        }))}
      />

      <Lista
        titulo="O que está concentrado"
        cor="border-white/10 bg-adega-surface-2"
        itens={analise.excessos.map((item) => ({ titulo: item.titulo, corpo: item.porque }))}
      />

      <Lista
        titulo="Na próxima compra"
        cor="border-adega-wine-light/40 bg-adega-wine/10"
        itens={analise.proximasCompras.map((item) => ({ titulo: item.oQue, corpo: item.porque }))}
      />
    </div>
  );
}

function Lista({
  titulo,
  cor,
  itens,
}: {
  titulo: string;
  cor: string;
  itens: Array<{ titulo: string; corpo: string; extra?: string }>;
}) {
  // Lista vazia não vira seção vazia: a IA pode não ter nada a dizer num dos
  // três, e um título solto pareceria erro.
  if (itens.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-adega-muted">{titulo}</h3>
      <ul className="space-y-2">
        {itens.map((item) => (
          <li key={item.titulo} className={`rounded-xl border p-3 ${cor}`}>
            <p className="text-sm font-medium">{item.titulo}</p>
            {item.corpo ? (
              <p className="mt-1 text-sm leading-relaxed text-adega-muted">{item.corpo}</p>
            ) : null}
            {item.extra ? <p className="mt-2 text-sm leading-relaxed">👉 {item.extra}</p> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
